import { Rank } from "./rank.js";
import { Country } from "./country.js";
import { Platform } from "./platform.js";

export type Keyword = {
    id: number;
    created: Date;
    updated: Date;

    term: string;
    country: Country;
    platform: Platform;

    // Data

    popularity?: number;
    popularityUpdated?: Date;

    recommendations?: KeywordRecommendation[];
    recommendationsUpdated?: Date;

    ranks?: Rank[];
    ranksUpdated?: Date;
    ranksUpdatedBefore?: Date;

    difficulty?: number;

    syncing?: boolean; // Transient field to indicate if the keyword is being synced
    ranksCount?: number; // Transient field to indicate the number of ranks for the keyword
    connectedAppRank?: number; // Transient field to indicate the rank of the connected app in a keyword list
    connectedAppRankBefore?: number; // Transient field to indicate the previous rank of the connected app in a keyword list
};

export type NaturalKeywordId = {
    term: string;
    country: Country;
    platform: Platform;
};

export function getNaturalKeywordId(term: string, country: Country, platform: Platform): NaturalKeywordId {
    return {
        term: term.trim().toLowerCase(),
        country,
        platform,
    };
}

export function getNaturalKeywordIdString(keyword: Keyword) {
    const trimmedAndEncodedTerm = encodeURIComponent(keyword.term.trim().toLowerCase());
    return `${trimmedAndEncodedTerm}_${keyword.country.toLowerCase()}_${keyword.platform.toLowerCase()}`;
}

// Static method to parse a KeywordId from a string
export function parseNaturalKeywordIdString(naturalKeywordIdString: string): NaturalKeywordId | null {
    // Split from the end to handle terms that might contain underscores
    const parts = naturalKeywordIdString.split("_");
    if (parts.length < 3) {
        return null;
    }

    // Take the last two parts as country and platform
    const platform = parts.pop() as Platform;
    const country = parts.pop() as Country;

    // Join the remaining parts back together as the term (which might contain underscores)
    const term = parts.join("_");
    if (!term || !country || !platform) {
        return null;
    }
    const encodedTerm = decodeURIComponent(term);
    return { term: encodedTerm, country, platform };
}

export function keywordHasAllApps(keyword: Keyword): boolean {
    const allApps = keyword.ranks?.filter((rank: Rank) => rank.app !== undefined) ?? [];
    const allRanks = keyword.ranks?.length ?? 0;
    return allApps.length === allRanks;
}

export function keywordNeedsRankUpdate(keyword: Keyword): boolean {
    const now = new Date();
    const ranksUpdated = keyword.ranksUpdated ?? new Date(1970, 0, 1);

    // Check if ranks need updating (new day has started)
    const lastUpdatedDay = new Date(ranksUpdated);
    lastUpdatedDay.setHours(0, 0, 0, 0);

    const currentDay = new Date(now);
    currentDay.setHours(0, 0, 0, 0);

    const needsRanksUpdate = currentDay.getTime() > lastUpdatedDay.getTime();

    // Only log after 9 AM to avoid cluttering nightly sync logs
    if (needsRanksUpdate && now.getHours() >= 6) {
        console.log(`[keywordNeedsRankUpdate] Keyword "${keyword.term}" (${keyword.country}/${keyword.platform}) needs rank update:`, {
            ranksUpdated: keyword.ranksUpdated,
            ranksUpdatedISO: keyword.ranksUpdated ? new Date(keyword.ranksUpdated).toISOString() : "never",
            now: now.toISOString(),
            lastUpdatedDayMidnight: lastUpdatedDay.toISOString(),
            currentDayMidnight: currentDay.toISOString(),
            lastUpdatedDayTime: lastUpdatedDay.getTime(),
            currentDayTime: currentDay.getTime(),
            difference: currentDay.getTime() - lastUpdatedDay.getTime(),
        });
    }

    return needsRanksUpdate;
}

/** Staleness threshold (in days) for popularity and recommendation data. */
const STALENESS_THRESHOLD_DAYS = 30;

export function keywordNeedsPopularityUpdate(keyword: Keyword): boolean {
    const now = new Date();
    const popularityUpdated = keyword.popularityUpdated ? new Date(keyword.popularityUpdated) : new Date(1970, 0, 1);

    const diffInDays = (now.getTime() - popularityUpdated.getTime()) / (1000 * 60 * 60 * 24);
    return diffInDays > STALENESS_THRESHOLD_DAYS;
}

export function keywordNeedsRecommendationUpdate(keyword: Keyword): boolean {
    const now = new Date();
    const recommendationsUpdated = keyword.recommendationsUpdated ? new Date(keyword.recommendationsUpdated) : new Date(1970, 0, 1);

    const diffInDays = (now.getTime() - recommendationsUpdated.getTime()) / (1000 * 60 * 60 * 24);
    return diffInDays > STALENESS_THRESHOLD_DAYS;
}

export function computeExactMatches(keyword: Keyword): number {
    const term = keyword.term?.toLowerCase() ?? "";
    if (!term) return 0;
    const top10 = (keyword.ranks ?? []).slice(0, 10);
    let count = 0;
    for (const rank of top10) {
        if (!rank.app?.localization) continue;
        const title = rank.app.localization.title?.toLowerCase() ?? "";
        const subtitle = rank.app.localization.subtitle?.toLowerCase() ?? "";
        if (title.includes(term) || subtitle.includes(term)) count++;
    }
    return count;
}

export type KeywordRecommendation = {
    name: string;
    popularity: number; // Recommendations do not have "real" keyword popularity
};
